import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isHttpError } from '@/api/http/errors'
import { queryKeys } from '@/api/query-keys'
import { dictionaryService } from '@/api/services/dictionary.service'
import type {
  ClearDictionaryOutcome,
  DictionaryClearResult,
  DictionaryResponse,
} from '@/api/types/dictionary'
import { logger } from '@/utils/logger'

const CLEAR_INCOMPLETE = 'DICTIONARY_CLEAR_INCOMPLETE'

/**
 * A partly failed clear answers with an error status (500, 502, 504 or 403 depending on the cause)
 * and a body naming what was removed and what remains, so it is read from the body, not the status.
 */
async function readIncompleteClear(error: unknown): Promise<DictionaryClearResult | null> {
  if (!isHttpError(error) || !error.response) return null

  const body = (await error.response.json().catch(() => null)) as {
    code?: unknown
    removed?: unknown
    remaining?: unknown
  } | null

  if (body?.code !== CLEAR_INCOMPLETE || !Array.isArray(body.remaining)) return null

  const words = (list: unknown[]) => list.filter((word): word is string => typeof word === 'string')

  return {
    removed: Array.isArray(body.removed) ? words(body.removed) : [],
    remaining: words(body.remaining),
  }
}

/**
 * Clears the book's custom words with one request and waits for the backend before touching the
 * cache, so a failed clear never shows an empty dictionary. The list then shows exactly the words
 * the backend reports as remaining: none after a full clear, the leftovers after a partial one. A
 * read already running is cancelled so it can't bring removed words back, and the dictionary is
 * read again so changes other editors made meanwhile still show.
 */
export function useClearDictionary() {
  const queryClient = useQueryClient()

  return useCallback(async (): Promise<ClearDictionaryOutcome> => {
    const showRemaining = (remaining: string[]) => {
      void queryClient.cancelQueries({ queryKey: queryKeys.dictionary.all })

      queryClient.setQueryData<DictionaryResponse>(queryKeys.dictionary.all, (current) =>
        current ? { ...current, customWords: remaining } : current
      )

      void queryClient.invalidateQueries({ queryKey: queryKeys.dictionary.all })
    }

    try {
      const result = await dictionaryService.clearWords()

      showRemaining(result.remaining)

      return result.remaining.length === 0
        ? { status: 'cleared', removedCount: result.removed.length }
        : { status: 'incomplete', remaining: result.remaining }
    } catch (error) {
      const incomplete = await readIncompleteClear(error)

      logger.error('Clearing the dictionary failed', error)

      if (!incomplete) return { status: 'failed' }

      showRemaining(incomplete.remaining)

      return { status: 'incomplete', remaining: incomplete.remaining }
    }
  }, [queryClient])
}
